import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Link,
  Routes,
  Route,
  useLocation,
  useNavigationType,
  useParams,
} from "react-router-dom";
import { StoryMenu } from "./StoryMenu";
import { HomePage } from "./HomePage";
import { SettingsModal } from "./SettingsMenu";
import StoryView from "./StoryView";
import { Settings, Theme, ShowTranslationMode, TextSize } from "./settings";
import { TopMenu } from "./TopMenu";
import { BottomTabBar } from "./Navigation";
import { GenerateStoryView } from "./GenerateView";
import { UploadView } from "./UploadView";
import { MyDictionaryView } from "./MyDictionaryView";
import { PracticeView } from "./PracticeView";
import { useLoggedIn, useLoggedInOptimistic } from "./firebase";
import { SignInPage, SignUpPage } from "./LoginPage";
import { useStoryQuery } from "./queries";
import { lstr } from "./localization";
import { usePageTitle } from "./pageTitle";
import { Pup } from "./Pup";
import { ReadAlong, useReadAlong } from "./ReadAlong";

const DARK_COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";

// Browser chrome color per theme; matches --color-cream in index.css.
const THEME_COLOR = { light: "#faf7f0", dark: "#181613" };

function applyTheme(theme: Theme) {
  const dark =
    theme === Theme.Dark ||
    (theme === Theme.System &&
      window.matchMedia(DARK_COLOR_SCHEME_QUERY).matches);
  document.documentElement.classList.toggle("dark", dark);
  // index.html ships one theme-color per system scheme; an explicit theme
  // choice overrides both.
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => {
      meta.content = dark ? THEME_COLOR.dark : THEME_COLOR.light;
    });
}

// The UI is rendered in the spoken language; the document language drives
// screen-reader pronunciation and browser features like translation offers.
function applySettingsToDocument(settings: Settings) {
  applyTheme(settings.theme);
  document.documentElement.lang = settings.lLocale;
}

const SUPPORTED_LOCALES = ["en", "de", "ru"];

const DEFAULT_SETTINGS: Settings = {
  lLocale: "en",
  rLocale: "de",
  showTranslation: true,
  showTranslationMode: ShowTranslationMode.ByParagraph,
  theme: Theme.System,
  colorNounGenders: true,
  textSize: TextSize.Medium,
};

// loadSettingsOrDefault tolerates corrupt or partial localStorage content:
// anything unparseable or invalid falls back per-field to the defaults, so a
// bad stored blob can never crash the app on startup.
function loadSettingsOrDefault(): Settings {
  let parsed: Partial<Settings> = {};
  const settingsString = localStorage.getItem("settings");
  if (settingsString) {
    try {
      const raw: unknown = JSON.parse(settingsString);
      if (raw && typeof raw === "object") {
        parsed = raw as Partial<Settings>;
      }
    } catch (err) {
      console.error("Failed to parse stored settings, using defaults", err);
    }
  }

  const settings: Settings = {
    lLocale: SUPPORTED_LOCALES.includes(parsed.lLocale as string)
      ? (parsed.lLocale as string)
      : DEFAULT_SETTINGS.lLocale,
    rLocale: SUPPORTED_LOCALES.includes(parsed.rLocale as string)
      ? (parsed.rLocale as string)
      : DEFAULT_SETTINGS.rLocale,
    showTranslation:
      typeof parsed.showTranslation === "boolean"
        ? parsed.showTranslation
        : DEFAULT_SETTINGS.showTranslation,
    showTranslationMode: Object.values(ShowTranslationMode).includes(
      parsed.showTranslationMode as ShowTranslationMode,
    )
      ? (parsed.showTranslationMode as ShowTranslationMode)
      : DEFAULT_SETTINGS.showTranslationMode,
    theme: Object.values(Theme).includes(parsed.theme as Theme)
      ? (parsed.theme as Theme)
      : DEFAULT_SETTINGS.theme,
    // colorNounGenders and textSize were added later than the rest of
    // Settings; users whose localStorage predates them get the defaults.
    colorNounGenders:
      typeof parsed.colorNounGenders === "boolean"
        ? parsed.colorNounGenders
        : DEFAULT_SETTINGS.colorNounGenders,
    textSize: Object.values(TextSize).includes(parsed.textSize as TextSize)
      ? (parsed.textSize as TextSize)
      : DEFAULT_SETTINGS.textSize,
  };
  if (settings.lLocale === settings.rLocale) {
    settings.rLocale = settings.lLocale === "en" ? "de" : "en";
  }
  applySettingsToDocument(settings);
  return settings;
}

// Window scroll positions by history entry, so Back/Forward return the reader
// to where they were, while every other navigation starts at the top - except
// a replace within the same page (view state kept in the search params), which
// stays where it is. Kept in memory: a reload starts at the top, same as a
// fresh visit.
const scrollPositionByLocationKey = new Map<string, number>();

function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPathnameRef = useRef(location.pathname);

  useLayoutEffect(() => {
    window.history.scrollRestoration = "manual";
  }, []);

  useLayoutEffect(() => {
    const rememberPosition = () =>
      scrollPositionByLocationKey.set(location.key, window.scrollY);
    if (
      navigationType === "REPLACE" &&
      location.pathname === previousPathnameRef.current
    ) {
      rememberPosition();
    } else {
      const savedPosition =
        navigationType === "POP"
          ? scrollPositionByLocationKey.get(location.key)
          : undefined;
      window.scrollTo(0, savedPosition ?? 0);
    }
    previousPathnameRef.current = location.pathname;

    window.addEventListener("scroll", rememberPosition, { passive: true });
    return () => window.removeEventListener("scroll", rememberPosition);
  }, [location.key, location.pathname, navigationType]);

  return null;
}

// AppShell is the single layout wrapper for every route: settings modal,
// header, the centered content column, and on the main sections the phone
// tab bar. Settings state itself lives in App so that a change made on one
// page is immediately visible on all others. The document itself scrolls (no
// inner scroll container), so keyboard paging, mobile browser toolbars and
// scroll restoration behave natively.
function AppShell({
  settings,
  setSettings,
  reader,
  showTabBar = false,
  contentClassName = "w-full max-w-[680px] pt-5 px-4",
  children,
}: {
  settings: Settings;
  setSettings: (value: Settings) => void;
  // Set on story pages: the header turns into the reader's toolbar.
  reader?: {
    title: { text: string; lang: string } | null;
    // True when the story has a translation in the user's spoken language,
    // so the translation section of the reader options applies.
    translationAvailable: boolean;
    readAlong: ReadAlong;
  };
  // The phone tab bar between the main sections, for logged-in users.
  showTabBar?: boolean;
  contentClassName?: string;
  children: React.ReactNode;
}) {
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const loggedIn = useLoggedInOptimistic();
  const tabBarVisible = showTabBar && loggedIn;

  return (
    <div className="flex min-h-screen flex-col">
      {showSettings && (
        <SettingsModal
          settings={settings}
          setSettings={setSettings}
          closeModal={() => setShowSettings(false)}
        />
      )}
      <TopMenu
        reader={reader}
        settings={settings}
        setSettings={setSettings}
        onOpenSettings={() => setShowSettings(true)}
      />
      <main
        className={`flex flex-grow flex-col items-center ${
          tabBarVisible ? "pb-[calc(4.5rem+env(safe-area-inset-bottom))] sm:pb-0" : ""
        }`}
      >
        <div className={`animate-enter ${contentClassName}`}>{children}</div>
      </main>
      {tabBarVisible && <BottomTabBar l={settings.lLocale} r={settings.rLocale} />}
    </div>
  );
}

interface RouteProps {
  settings: Settings;
  setSettings: (value: Settings) => void;
}

function StoryMenuComponent({ settings, setSettings }: RouteProps) {
  // Optimistic so a returning logged-in user doesn't see the signup-oriented
  // landing page flash on a cold load while Firebase restores the session
  // (and a logged-out visitor still gets the landing page instantly).
  const loggedIn = useLoggedInOptimistic();
  usePageTitle(null);

  return (
    <AppShell settings={settings} setSettings={setSettings} showTabBar={true}>
      {loggedIn ? (
        <StoryMenu l={settings.lLocale} r={settings.rLocale} />
      ) : (
        // Logged-out visitors get the conversion-oriented landing page.
        <HomePage settings={settings} setSettings={setSettings} />
      )}
    </AppShell>
  );
}

function StoryComponent({ settings, setSettings }: RouteProps) {
  const { storyId } = useParams();

  // Same query key as inside StoryView; TanStack Query dedupes the request so
  // we get the response here for free: the reader toolbar shows the title,
  // hides the translation section of the reader options when the story
  // has no L localization, and reads the story aloud.
  const storyQuery = useStoryQuery(storyId!, settings.lLocale, settings.rLocale);
  const translationAvailable =
    storyQuery.data?.localizations.has(settings.lLocale) ?? false;
  const rStory = storyQuery.data?.localizations.get(settings.rLocale);
  const rTitle = rStory?.title;
  usePageTitle(rTitle ?? null);
  // Lives here, above both the toolbar's Listen button and the story text
  // that follows the sentence being read.
  const readAlong = useReadAlong(storyId!, settings.rLocale, rStory);

  return (
    <AppShell
      settings={settings}
      setSettings={setSettings}
      reader={{
        title: rTitle ? { text: rTitle, lang: settings.rLocale } : null,
        translationAvailable,
        readAlong,
      }}
      contentClassName="w-full max-w-[680px] pt-4 px-3 sm:px-4"
    >
      <StoryView
        // Fresh reader state (open popup, focused word) per story and pair.
        key={`${storyId}:${settings.lLocale}:${settings.rLocale}`}
        storyId={storyId!}
        l={settings.lLocale}
        r={settings.rLocale}
        shouldShowTranslation={settings.showTranslation}
        showTranslationBySentence={
          settings.showTranslationMode === ShowTranslationMode.BySentence
        }
        colorNounGenders={settings.colorNounGenders}
        textSize={settings.textSize}
        readAlongSentenceIndex={readAlong.sentenceIndex}
        onWordPopupOpen={readAlong.pause}
      />
    </AppShell>
  );
}

function StoryGenerateComponent({ settings, setSettings }: RouteProps) {
  usePageTitle(lstr(settings.lLocale).generate_page_title);
  return (
    <AppShell
      settings={settings}
      setSettings={setSettings}
      contentClassName="w-full max-w-[900px] pt-5 px-4"
    >
      <GenerateStoryView l={settings.lLocale} r={settings.rLocale} />
    </AppShell>
  );
}

function UploadComponent({ settings, setSettings }: RouteProps) {
  usePageTitle(lstr(settings.lLocale).upload_page_title);
  return (
    <AppShell
      settings={settings}
      setSettings={setSettings}
      contentClassName="w-full max-w-[900px] pt-5 px-4 pb-10"
    >
      <UploadView l={settings.lLocale} r={settings.rLocale} />
    </AppShell>
  );
}

// LoginPrompt stands in for a page that needs an account.
function LoginPrompt({ l }: { l: string }) {
  return (
    <div className="mt-10 flex flex-col items-center gap-4 text-center">
      <Pup className="h-20 w-20" shape="circle" />
      <p className="max-w-xs text-secondary-text">
        {lstr(l).my_dictionary_login_prompt}
      </p>
      <div className="flex gap-2">
        <Link to="/signup" className="btn-primary px-5 py-2.5">
          {lstr(l).signup_button}
        </Link>
        <Link to="/login" className="btn-secondary px-5 py-2.5">
          {lstr(l).login_button}
        </Link>
      </div>
    </div>
  );
}

function MyDictionaryComponent({ settings, setSettings }: RouteProps) {
  const loggedIn = useLoggedIn();
  usePageTitle(lstr(settings.lLocale).nav_words);

  return (
    <AppShell settings={settings} setSettings={setSettings} showTabBar={true}>
      {loggedIn ? (
        <MyDictionaryView l={settings.lLocale} r={settings.rLocale} />
      ) : (
        <LoginPrompt l={settings.lLocale} />
      )}
    </AppShell>
  );
}

function PracticeComponent({ settings, setSettings }: RouteProps) {
  const loggedIn = useLoggedIn();
  usePageTitle(lstr(settings.lLocale).practice_nav);

  return (
    <AppShell settings={settings} setSettings={setSettings} showTabBar={true}>
      {loggedIn ? (
        // Fresh session state per language pair.
        <PracticeView
          key={`${settings.lLocale}:${settings.rLocale}`}
          l={settings.lLocale}
          r={settings.rLocale}
        />
      ) : (
        <LoginPrompt l={settings.lLocale} />
      )}
    </AppShell>
  );
}

function LoginComponent({ settings, setSettings }: RouteProps) {
  usePageTitle(lstr(settings.lLocale).login_button);
  return (
    <AppShell settings={settings} setSettings={setSettings}>
      <SignInPage l={settings.lLocale} />
    </AppShell>
  );
}

function SignUpComponent({ settings, setSettings }: RouteProps) {
  usePageTitle(lstr(settings.lLocale).signup_button);
  return (
    <AppShell settings={settings} setSettings={setSettings}>
      <SignUpPage l={settings.lLocale} />
    </AppShell>
  );
}

function App() {
  const [settings, setSettingsState] = useState<Settings>(loadSettingsOrDefault);

  const setSettings = (value: Settings) => {
    if (value.lLocale === value.rLocale) {
      value = { ...value, rLocale: value.lLocale === "en" ? "de" : "en" };
    }
    localStorage.setItem("settings", JSON.stringify(value));
    applySettingsToDocument(value);
    setSettingsState(value);
  };

  // In System mode, follow OS light/dark switches while the app is open.
  useEffect(() => {
    if (settings.theme !== Theme.System) return;
    const darkColorSchemeQuery = window.matchMedia(DARK_COLOR_SCHEME_QUERY);
    const followSystemTheme = () => applyTheme(Theme.System);
    darkColorSchemeQuery.addEventListener("change", followSystemTheme);
    return () =>
      darkColorSchemeQuery.removeEventListener("change", followSystemTheme);
  }, [settings.theme]);

  const routeProps = { settings, setSettings };

  return (
    <BrowserRouter>
      <ScrollRestoration />
      <Routes>
        <Route path="/" element={<StoryMenuComponent {...routeProps} />} />
        <Route path="/login" element={<LoginComponent {...routeProps} />} />
        <Route path="/signup" element={<SignUpComponent {...routeProps} />} />
        <Route path="/generate" element={<StoryGenerateComponent {...routeProps} />} />
        <Route path="/upload" element={<UploadComponent {...routeProps} />} />
        <Route path="/dictionary" element={<MyDictionaryComponent {...routeProps} />} />
        <Route path="/practice" element={<PracticeComponent {...routeProps} />} />
        <Route path="/generated/:storyId" element={<StoryComponent {...routeProps} />} />
        <Route path="/:storyId" element={<StoryComponent {...routeProps} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
